import { useEffect, useState } from "react";
import { BarChart3, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import NabhManual from "@/components/NabhManual";
import ImportDialog from "@/components/ImportDialog";

export default function Reports() {
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        api.get("/nabh/indicators").then(({ data }) => setData(data)).catch((e) => toast.error(fmtErr(e)));
    }, []);

    const downloadCsv = async () => {
        setDownloading(true);
        try {
            const res = await axios.get(`${API}/nabh/indicators/csv`, { responseType: "blob", withCredentials: true });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "nabh_indicators.csv";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Indicators CSV downloaded");
        } catch (e) { toast.error(fmtErr(e)); } finally { setDownloading(false); }
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="reports-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">NABH Reports</h1>
                    <p className="mt-1 text-base text-slate-500">Quality & accreditation indicators computed from your records</p>
                </div>
                <div className="flex gap-2">
                    <ImportDialog module="nabh" label="Import" onDone={() => api.get("/nabh/indicators").then(({ data }) => setData(data)).catch(() => {})} />
                    <Button data-testid="nabh-csv-btn" variant="outline" onClick={downloadCsv} disabled={downloading || !data} className="gap-1.5">
                        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
                    </Button>
                </div>
            </div>

            {!data ? (
                <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" /></div>
            ) : (
                <>
                    <p className="text-xs text-slate-400" data-testid="nabh-computed-at">Computed: {data.computed_at.slice(0, 19).replace("T", " ")} UTC · Practice-wide (all doctors)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="nabh-indicators-grid">
                        {data.indicators.map((i) => (
                            <div key={i.key} data-testid={`nabh-${i.key}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <div className="flex items-center gap-2 mb-2">
                                    <BarChart3 className="h-4 w-4 text-[#E06D1F]" />
                                    <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{i.label}</p>
                                </div>
                                <p className="font-heading text-3xl font-extrabold text-slate-900">{i.value}</p>
                                <p className="mt-1 text-xs text-slate-500">{i.detail}</p>
                            </div>
                        ))}
                    </div>
                    <NabhManual />
                    <div className="rounded-xl border border-orange-200 bg-[#FFF4EC] p-4 text-sm text-slate-700" data-testid="nabh-note">
                        <b>NABH note:</b> These indicators map to standard accreditation KPIs (ALOS, documentation completeness, allergy capture). Keep discharge summaries and allergy fields updated to stay audit-ready.
                    </div>
                </>
            )}
        </div>
    );
}
